import { useEffect, useContext, useMemo } from "react";
import type { ElementType, ReactNode } from "react";

import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";
import { JoinedCampaigns as JoinedCampaignsBase } from "@/features/campaigns/digital/joined-digital-campaign-list";
import { DigitalCampaignList } from "@/features/campaigns/digital/digital-campaign-list";
import PageLayout from "@/components/layouts/page/PageLayout";
import PageHeaderBase from "@/components/layouts/pageHeaders/PageHeader";
import UniversalWidgetTemplateBase from "@/components/templates/UniversalWidgetTemplate";
import { reportService } from "@/shared/services/reportService";
import { renderTemplate } from "@/shared/helpers/reportHelper";
import NoDataFound from "@/components/NoDataFound";
import { DirectMailCampaignList } from "@/features/campaigns/direct-mail/direct-mail-campaign-list";
import { PostcardCampaignJoined as PostcardCampaignJoinedBase } from "@/features/campaigns/direct-mail/joined-direct-mail-campaign-list";
import useFetcher from "@/helpers/hooks/useFetcher";
import { CAMPAIGN_CONST, CUSTOM_WIDGET_PARAM } from "@/config/constants";
import { removeCampaignByNameLike } from "@/helpers/utils";

const JoinedCampaigns = JoinedCampaignsBase as ElementType;
const PageHeader = PageHeaderBase as ElementType;
const PostcardCampaignJoined = PostcardCampaignJoinedBase as ElementType;
const UniversalWidgetTemplate = UniversalWidgetTemplateBase as ElementType;

type UserRecord = Record<string, any>;
type BrandRecord = Record<string, any>;
type WidgetRecord = Record<string, any>;
type GlobalContextValue = {
  loggedInUser?: UserRecord;
  selectedBrand?: BrandRecord;
};
type Loader = (_show: boolean) => void;

const ParticipantDashboardPage = () => {
  const { loggedInUser, selectedBrand } = useContext(
    GlobalContext
  ) as GlobalContextValue;
  const loader = useContext(LoaderContext) as Loader;

  const reqData = useMemo(() => {
    return {
      id: selectedBrand?.id,
      fk_participant_id: loggedInUser?.participantData?.id,
      userId: loggedInUser?.id,
    };
  }, [selectedBrand, loggedInUser]);

  const { data, isLoading, isValidating } = useFetcher(
    reqData,
    reportService.getDashboardReport,
    { revalidateOnFocus: false }
  );

  const templateData = data;

  useEffect(() => {
    loader(isLoading || isValidating);
  }, [isLoading, isValidating, loader]);

  const renderTable = (widget: WidgetRecord): ReactNode => {
    if (widget.id === 148 && widget?.data) {
      return (
        <DigitalCampaignList
          selectedBrand={selectedBrand}
          isThereAnyActiveSubscription={
            templateData?.isThereAnyActiveSubscription
          }
          campaigns={widget?.data?.unJoinedCampaign || []}
        />
      );
    } else if (widget.id === 149 && widget?.data) {
      const noBoostCamp = removeCampaignByNameLike(
        widget?.data?.joinedCampaign,
        ["boost", "boost:"]
      );
      return <JoinedCampaigns joinedCampaigns={noBoostCamp} />;
    } else if (widget.id === 190 && widget?.data) {
      const notDraftCampaigns =
        widget?.data?.unJoinedCampaign?.filter(
          (campaign: WidgetRecord) => campaign.participant_campaign === null
        ) || [];

      const alwaysAvailableCampaigns = notDraftCampaigns?.filter(
        (campaign: WidgetRecord) =>
          campaign.campaign_type === CAMPAIGN_CONST.POSTCARD.TYPE.EVERGREEN
      );

      const limitedTimeCampaigns = notDraftCampaigns?.filter(
        (campaign: WidgetRecord) =>
          campaign.campaign_type === CAMPAIGN_CONST.POSTCARD.TYPE.LIMITED
      );

      const draftCampaigns =
        widget?.data?.unJoinedCampaign?.filter(
          (campaign: WidgetRecord) => campaign.participant_campaign !== null
        ) || [];

      return (
        <DirectMailCampaignList
          campaigns={[
            ...alwaysAvailableCampaigns,
            ...limitedTimeCampaigns,
            ...draftCampaigns,
          ]}
        />
      );
    } else if (widget.id === 191 && widget?.data) {
      const noBoostCamp = removeCampaignByNameLike(
        widget?.data?.joinedCampaign,
        ["boost", "boost:"]
      );
      return <PostcardCampaignJoined joinedCampaigns={noBoostCamp} />;
    }
    return null;
  };

  const renderPage = () => {
    if (isLoading && !templateData) return <NoDataFound message="Loading..." />;
    else if (!templateData) return <NoDataFound />;
    return (
      <div className="dashboard participant-dashboard">
        <UniversalWidgetTemplate
          type="participant"
          embedWidgetParams={{
            [CUSTOM_WIDGET_PARAM.ParticipantID]:
              loggedInUser?.participantData?.system_generated_participant_id,
          }}
          isTemplateData={templateData}
          hideDateSelector
        />
        {renderTemplate({
          report: templateData,
          renderTable,
          embedWidgetParams: {
            [CUSTOM_WIDGET_PARAM.BrandID]:
              selectedBrand?.system_generated_brand_id,
            [CUSTOM_WIDGET_PARAM.ParticipantID]:
              loggedInUser?.participantData?.system_generated_participant_id,
          },
        })}
      </div>
    );
  };

  return (
    <PageLayout className="participant-dashboard-container mx-0 mx-sm-2">
      <PageHeader showName showLastUpdate={false} showButton={true} />
      {renderPage()}
    </PageLayout>
  );
};

export default ParticipantDashboardPage;
